import * as React from 'react'
import { useEffect, useMemo } from 'react'
import Loading from '@doordash/component-loading'
import { StackChildren, Stack, Text, Spacing } from '@doordash/design-language'
import NavBar from '@doordash/component-navbar'
import { FixedBottomContainer } from '../../atoms/fixed-bottom-container'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { OsTypes } from '../../../webview/types'
import { detectWebviewOs } from '../../../utilities/userAgent'
import {
  IOS,
  ANDROID,
  CLOSE,
  HELP_URL,
  DasherObjectFields,
  BUSY_IMG_URL,
  SCHEDULE_DASH_IMG_URL,
  DASH_IMG_URL,
} from './constants'
import t from './strings'
import { FirstDashButtons, FirstDashContent } from './'
import { loadUrl } from '../../../webview'
import * as actions from '../../../actions'
import * as events from '../../../events'
import * as DA from '../../../typeModules/events'
import { get } from 'lodash'
import { BusynessStatus } from '../../../models/types'
import { getDasherStartingPointDetails } from './getDasherStartingPoint'

type FirstDashProps = {
  isSubmarketBusy: boolean
  hasDashNowBenefit: boolean
  dasher?: {
    isLoading: boolean
    rewardsTier?: string
    defaultStartingPoint: number
  }
  startingPoint: { id: string; isLoading: boolean }
  startingPointStatus?: StartingPoint[]
  loadDasherSerializer?: (fields: string[]) => Promise<void>
  loadStartingPointStatus: ({
    lat,
    lng,
  }?: {
    lat: number | null
    lng: number | null
  }) => Promise<void>
  loadStartingPointByPoint: ({ lat, lng }) => Promise<void>
  track?: (
    name: DA.EventActions,
    page: DA.ActivatedNoFirstDashScreens,
    data?: object
  ) => Promise<void>
  experiments: object
}

type StartingPoint = {
  id: number
  name: string
  location: {
    lat: number
    lng: number
  }
  vehicle_dash_status: VehicleDashStatus[]
}

type VehicleDashStatus = {
  vehicle_type: number
  busyness_status: string
}
type ImageContainerProps = {
  url: string
}

const ImageContainer = styled.div`
  height: 35vh;
  width: 100%;
  background-position: 50%;
  background-size: 70%;
  background-image: ${(props: ImageContainerProps) => `url(${props.url})`};
  background-repeat: no-repeat;
`

const ContentContainer = styled.div`
  padding-left: ${Spacing.Small}px;
  padding-right: ${Spacing.Small}px;
`

const ScrollContainer = styled.div`
  height: 80vh;
  overflow-y: scroll;
`

const FirstDash: React.FC<FirstDashProps> = ({
  loadDasherSerializer,
  loadStartingPointStatus,
  loadStartingPointByPoint,
  dasher,
  startingPoint,
  startingPointStatus,
  track,
  experiments,
}) => {
  const [currentLocation, setCurrentLocation] = React.useState<{
    lat: number | null
    lng: number | null
  }>({
    lat: null,
    lng: null,
  })

  const isLocationEnabled = Boolean(navigator && navigator.geolocation)

  const isLocationByStartingPointEnabled = get(
    experiments,
    'dx_activated_no_first_dash_location',
    false
  )

  useEffect(() => {
    const getLocation = () => {
      const loadDefaultStartingPoint = () =>
        loadStartingPointStatus({ lat: null, lng: null })

      if (!isLocationEnabled) {
        track(DA.EventActions.Rendered, DA.ActivatedNoFirstDashScreens.Dash, {
          gps: 'disabled',
        })
        loadDasherSerializer([
          DasherObjectFields.RewardsTier,
          DasherObjectFields.DefaultStartingPoint,
        ])
        loadDefaultStartingPoint()
        return
      }

      try {
        navigator.geolocation.getCurrentPosition(
          position => {
            const coordinates = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            }
            track(
              DA.EventActions.Success,
              DA.ActivatedNoFirstDashScreens.Dash,
              {
                gps: DA.EventActions.Success,
                coordinates,
              }
            )
            loadDasherSerializer([
              DasherObjectFields.RewardsTier,
              DasherObjectFields.DefaultStartingPoint,
            ])
            setCurrentLocation(coordinates)
            if (isLocationByStartingPointEnabled) {
              loadStartingPointByPoint(coordinates)
            }
            loadStartingPointStatus(coordinates)
          },
          e => {
            // Error State on fetching GPS Coordinates, defaulting to Dasher Object SP
            track(
              DA.EventActions.Rendered,
              DA.ActivatedNoFirstDashScreens.Dash,
              {
                gps: 'errorFetchingLocation',
                error: e,
              }
            )
          }
        )
      } catch (e) {
        track(DA.EventActions.Error, DA.ActivatedNoFirstDashScreens.Dash, {
          gps: 'errorGetCurrentPosition',
          error: e,
        })
      }
    }
    getLocation()
  }, [
    isLocationByStartingPointEnabled,
    isLocationEnabled,
    loadStartingPointByPoint,
    loadStartingPointStatus,
    loadDasherSerializer,
    track,
  ])

  const currentStartingPointStatus = useMemo(() => {
    const dasherStartingPoint = get(dasher, 'defaultStartingPoint')

    return getDasherStartingPointDetails(
      { currentLat: currentLocation.lat, currentLng: currentLocation.lng },
      startingPointStatus,
      dasherStartingPoint,
      isLocationEnabled
    )
  }, [dasher, currentLocation, startingPointStatus, isLocationEnabled])

  if (
    dasher.isLoading ||
    startingPoint.isLoading ||
    !currentStartingPointStatus ||
    !startingPointStatus
  ) {
    return <Loading />
  }
  const onClickHelpUrl = () => {
    track(DA.EventActions.Clicked, DA.ActivatedNoFirstDashScreens.Dash, {
      name: 'help',
    })

    loadUrl(`${HELP_URL}`)
  }

  const onClose = () => {
    track(DA.EventActions.Close, DA.ActivatedNoFirstDashScreens.Dash)
    const os = detectWebviewOs()
    const prefix = os === OsTypes.iOS ? IOS : ANDROID
    window.location.assign(`${prefix}${CLOSE}`)
  }

  const busynessStatus = get(
    currentStartingPointStatus,
    'vehicle_dash_status[0].busyness_status',
    BusynessStatus.Normal
  )

  const isSubmarketBusy =
    busynessStatus === BusynessStatus.Busy ||
    busynessStatus === BusynessStatus.VeryBusy

  const startingPointByPointId = get(startingPoint, 'id', '')
  const currentStartingPointStatusId = get(currentStartingPointStatus, 'id', '')

  const startingPointId = isLocationByStartingPointEnabled
    ? startingPointByPointId
    : currentStartingPointStatusId

  const isDasherInStartingPoint = () => {
    if (isLocationByStartingPointEnabled) {
      return startingPointId !== ''
    }
    return true
  }

  const hasDashNowBenefit = get(dasher, 'rewardsTier', null) !== null

  const imgUrl = () => {
    if (!isDasherInStartingPoint) return SCHEDULE_DASH_IMG_URL
    if (hasDashNowBenefit) return DASH_IMG_URL
    if (isSubmarketBusy) return BUSY_IMG_URL

    return SCHEDULE_DASH_IMG_URL
  }

  if (
    isLocationByStartingPointEnabled &&
    currentStartingPointStatusId &&
    startingPointByPointId !== currentStartingPointStatusId
  ) {
    // Adding realtime tracking for where this feature prevented a checkin error
    track(DA.EventActions.Rendered, DA.ActivatedNoFirstDashScreens.Dash, {
      name: 'mismatch',
      startingPointByPointId,
      currentStartingPointStatusId,
      currentLocation,
    })
  }

  return (
    <div>
      <NavBar
        onNavigationClick={onClose}
        navigationType={NavBar.NavigationTypes.Close}
        isSeparatorVisible={false}
      />
      <ScrollContainer>
        <ImageContainer url={imgUrl()} />
        <ContentContainer>
          <StackChildren>
            <Text styles={Text.Styles.PageTitle2}>{t.title()}</Text>
            <FirstDashContent
              isSubmarketBusy={isSubmarketBusy}
              hasDashNowBenefit={hasDashNowBenefit}
              isInStartingPoint={isDasherInStartingPoint()}
            />
            <Text
              textDecoration={Text.TextDecorations.Underline}
              weight={Text.Weights.Bold}
              tag={'a'}
              styles={Text.Styles.Medium16}
              onClick={onClickHelpUrl}
            >
              {t.helpCenter()}
            </Text>
          </StackChildren>
        </ContentContainer>
      </ScrollContainer>
      <FixedBottomContainer>
        <Stack>
          <ContentContainer>
            <FirstDashButtons
              startingPointId={startingPointId}
              startingPointStatus={currentStartingPointStatus}
              isSubmarketBusy={isSubmarketBusy}
              hasDashNowBenefit={hasDashNowBenefit}
              track={track}
            />
          </ContentContainer>
        </Stack>
      </FixedBottomContainer>
    </div>
  )
}

const mapStateToProps = state => ({
  dasher: state.dasher,
  startingPointStatus: state.startingPointStatus,
  startingPoint: state.startingPoint,
  experiments: state.experiments,
})

const mapDispatchToProps = {
  loadDasherSerializer: actions.loadDasherSerializer,
  loadStartingPointStatus: actions.loadStartingPointStatus,
  loadStartingPointByPoint: actions.loadStartingPointByPoint,
  track: events.trackActivatedNoFirstDash,
}

export default connect(mapStateToProps, mapDispatchToProps)(FirstDash)
